import type { ReactNode } from "react"

const DEFAULT_STROKE = "#7A746B"

interface IconProps {
  size?: number
  stroke?: string
  strokeWidth?: number
}

const Svg = ({
  size,
  children,
}: {
  size: number
  children: ReactNode
}) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
    {children}
  </svg>
)

export function SearchIcon({
  size = 16,
  stroke = DEFAULT_STROKE,
  strokeWidth = 1.4,
}: IconProps) {
  return (
    <Svg size={size}>
      <circle cx="7" cy="7" r="4.5" stroke={stroke} strokeWidth={strokeWidth} />
      <path
        d="M10.5 10.5L13.5 13.5"
        stroke={stroke}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
      />
    </Svg>
  )
}

export function BellIcon({
  size = 16,
  stroke = DEFAULT_STROKE,
  strokeWidth = 1.4,
}: IconProps) {
  return (
    <Svg size={size}>
      <path
        d="M3.5 11.5C3.5 11.5 4 10.5 4 8C4 5.791 5.791 4 8 4C10.209 4 12 5.791 12 8C12 10.5 12.5 11.5 12.5 11.5H3.5Z"
        stroke={stroke}
        strokeWidth={strokeWidth}
        strokeLinejoin="round"
      />
      <path
        d="M6.5 13.5C6.7 13.9 7.3 14.5 8 14.5C8.7 14.5 9.3 13.9 9.5 13.5"
        stroke={stroke}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
      />
    </Svg>
  )
}

export function DashboardIcon({
  size = 16,
  stroke = DEFAULT_STROKE,
  strokeWidth = 1.4,
}: IconProps) {
  return (
    <Svg size={size}>
      <rect
        x="2.5"
        y="8.5"
        width="3"
        height="5"
        rx="0.5"
        stroke={stroke}
        strokeWidth={strokeWidth}
      />
      <rect
        x="6.5"
        y="5.5"
        width="3"
        height="8"
        rx="0.5"
        stroke={stroke}
        strokeWidth={strokeWidth}
      />
      <rect
        x="10.5"
        y="2.5"
        width="3"
        height="11"
        rx="0.5"
        stroke={stroke}
        strokeWidth={strokeWidth}
      />
    </Svg>
  )
}

export function SparkleIcon({
  size = 16,
  stroke = DEFAULT_STROKE,
  strokeWidth = 1.4,
}: IconProps) {
  return (
    <Svg size={size}>
      <path
        d="M8 2L9.2 6.8L14 8L9.2 9.2L8 14L6.8 9.2L2 8L6.8 6.8L8 2Z"
        stroke={stroke}
        strokeWidth={strokeWidth}
        strokeLinejoin="round"
      />
    </Svg>
  )
}

export function DealsIcon({
  size = 16,
  stroke = DEFAULT_STROKE,
  strokeWidth = 1.4,
  outlineOnly = false,
}: IconProps & { outlineOnly?: boolean }) {
  return (
    <Svg size={size}>
      <path
        d="M3.5 5L8 2L12.5 5V11L8 14L3.5 11V5Z"
        stroke={stroke}
        strokeWidth={strokeWidth}
        strokeLinejoin="round"
      />
      {outlineOnly ? null : (
        <path
          d="M8 8L12.5 5M8 8V14M8 8L3.5 5"
          stroke={stroke}
          strokeWidth={strokeWidth}
          strokeLinejoin="round"
        />
      )}
    </Svg>
  )
}

export function ContactsIcon({
  size = 16,
  stroke = DEFAULT_STROKE,
  strokeWidth = 1.4,
}: IconProps) {
  return (
    <Svg size={size}>
      <circle cx="8" cy="6" r="2.5" stroke={stroke} strokeWidth={strokeWidth} />
      <path
        d="M3 13.5C3 11.015 5.239 9 8 9C10.761 9 13 11.015 13 13.5"
        stroke={stroke}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
      />
    </Svg>
  )
}

export function CompaniesIcon({
  size = 16,
  stroke = DEFAULT_STROKE,
  strokeWidth = 1.2,
}: IconProps) {
  return (
    <Svg size={size}>
      <path
        d="M2.5 14V3.5C2.5 3.22 2.72 3 3 3H9C9.28 3 9.5 3.22 9.5 3.5V14M9.5 14V7.5C9.5 7.22 9.72 7 10 7H13C13.28 7 13.5 7.22 13.5 7.5V14M2.5 14H13.5M5 6H7M5 9H7M5 12H7M11 10H12M11 12H12"
        stroke={stroke}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
      />
    </Svg>
  )
}

export function InboxIcon({
  size = 16,
  stroke = DEFAULT_STROKE,
  strokeWidth = 1.4,
}: IconProps) {
  return (
    <Svg size={size}>
      <path
        d="M2 9.5V12.5C2 13.05 2.45 13.5 3 13.5H13C13.55 13.5 14 13.05 14 12.5V9.5"
        stroke={stroke}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
      />
      <path
        d="M2 9.5L3.5 3.5C3.61 3.06 4 2.75 4.45 2.75H11.55C12 2.75 12.39 3.06 12.5 3.5L14 9.5"
        stroke={stroke}
        strokeWidth={strokeWidth}
        strokeLinejoin="round"
      />
      <path
        d="M2 9.5H6L7 11H9L10 9.5H14"
        stroke={stroke}
        strokeWidth={strokeWidth}
        strokeLinejoin="round"
      />
    </Svg>
  )
}

export function ChevronLeftIcon({
  size = 14,
  stroke = DEFAULT_STROKE,
  strokeWidth = 1.6,
}: IconProps) {
  return (
    <Svg size={size}>
      <path
        d="M10 4L6 8L10 12"
        stroke={stroke}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Svg>
  )
}

export function ChevronRightIcon({
  size = 14,
  stroke = DEFAULT_STROKE,
  strokeWidth = 1.6,
}: IconProps) {
  return (
    <Svg size={size}>
      <path
        d="M6 4L10 8L6 12"
        stroke={stroke}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Svg>
  )
}

export function HamburgerIcon({
  size = 20,
  stroke = "#1E1B17",
  strokeWidth = 1.8,
}: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path
        d="M4 7H20M4 12H20M4 17H14"
        stroke={stroke}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
      />
    </svg>
  )
}

export function ClockIcon({
  size = 16,
  stroke = DEFAULT_STROKE,
  strokeWidth = 1.4,
}: IconProps) {
  return (
    <Svg size={size}>
      <circle cx="8" cy="8" r="5.5" stroke={stroke} strokeWidth={strokeWidth} />
      <path
        d="M8 4.5V8L10.5 9.5"
        stroke={stroke}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Svg>
  )
}

export function SettingsIcon({
  size = 16,
  stroke = DEFAULT_STROKE,
  strokeWidth = 1.4,
}: IconProps) {
  return (
    <Svg size={size}>
      <circle cx="8" cy="8" r="2" stroke={stroke} strokeWidth={strokeWidth} />
      <path
        d="M8 1.5V3.5M8 12.5V14.5M14.5 8H12.5M3.5 8H1.5M12.6 3.4L11.2 4.8M4.8 11.2L3.4 12.6M12.6 12.6L11.2 11.2M4.8 4.8L3.4 3.4"
        stroke={stroke}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
      />
    </Svg>
  )
}
